// services/api.js
const API_BASE = (
  process.env.REACT_APP_API_BASE || "http://localhost:8000"
).replace(/\/+$/, "");
const ASSET_BASE_URL = (process.env.REACT_APP_ASSET_BASE_URL || "").replace(
  /\/+$/,
  "",
);

const BACKEND_ASSET_PREFIXES = ["output_masks/", "uploads/"];
function isBackendAssetPath(path) {
  const normalized = String(path).replace(/^\/+/, "");
  return BACKEND_ASSET_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

export function toAssetUrl(path) {
  if (!path) return null;

  let assetPath = String(path).trim();

  if (/^https?:\/\//i.test(assetPath)) {
    try {
      const url = new URL(assetPath);
      const appUrl = new URL(window.location.href);
      const apiUrl = new URL(API_BASE);
      const isLocalDev = ["localhost", "127.0.0.1"].includes(appUrl.hostname);
      const isBackendAsset = isBackendAssetPath(url.pathname);

      if (
        isLocalDev &&
        isBackendAsset &&
        url.hostname === apiUrl.hostname &&
        url.port === apiUrl.port
      ) {
        assetPath = `${url.pathname}${url.search}`;
      }
    } catch (err) {
      // Fall through to the existing URL handling below.
    }

    if (!/^https?:\/\//i.test(assetPath)) {
      return assetPath;
    }

    if (ASSET_BASE_URL && assetPath.startsWith(ASSET_BASE_URL)) {
      return assetPath;
    }
    if (assetPath.startsWith(API_BASE)) {
      assetPath = assetPath.slice(API_BASE.length) || "/";
    } else {
      // Already an absolute URL on another origin (or frontend CDN) — leave it alone.
      return assetPath;
    }
  }

  // Webpack data/blob URLs and already-absolute app asset paths like /static/media/...
  if (
    assetPath.startsWith("data:") ||
    assetPath.startsWith("blob:") ||
    assetPath.startsWith("/static/")
  ) {
    return assetPath;
  }

  const relativePath = `/${assetPath.replace(/^\/+/, "")}`;

  // Only prefix backend-hosted assets; never /static/media bundled by CRA.
  if (ASSET_BASE_URL && isBackendAssetPath(relativePath)) {
    return `${ASSET_BASE_URL}${relativePath}`;
  }

  return relativePath;
}

export async function generateMask(imageFile) {
  const formData = new FormData();
  formData.append("room_image", imageFile);

  const res = await fetch(`${API_BASE}/generate-mask`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) throw new Error("Failed to generate mask");
  return res.json();
}

export async function getDemoRoom(roomId) {
  const res = await fetch(`${API_BASE}/demo-room/${roomId}`);
  console.log("Response: ", res);
  if (!res.ok) throw new Error("Failed to load demo room");
  return res.json();
}

// Retailer Product APIs
export async function addTile(formData) {
  const res = await fetch(`${API_BASE}/api/retailer/products/add-tile`, {
    method: "POST",
    credentials: 'include', 
    body: formData,
  });

  if (!res.ok) throw new Error("Failed to add tile");
  return res.json();
}

export async function bulkUploadProducts(file) {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_BASE}/api/retailer/products/bulk-upload`, {
    method: "POST",
    credentials: 'include', 
    body: formData,
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(
      data?.detail?.message || data?.detail || "Failed to upload products",
    );
  }

  return data;
}

export async function addWallpaper(formData) {
  const res = await fetch(`${API_BASE}/api/retailer/products/add-wallpaper`, {
    method: "POST",
    credentials: 'include', 
    body: formData,
  });

  if (!res.ok) throw new Error("Failed to add wallpaper");
  return res.json();
}

export async function addSize(formData) {
  const res = await fetch(`${API_BASE}/api/retailer/products/add-size`, {
    method: "POST",
    credentials: 'include',
    body: formData,
  });

  if (!res.ok) throw new Error("Failed to add size");
  return res.json();
}

export async function getSizes() {
  const res = await fetch(`${API_BASE}/api/retailer/products/sizes`, {
    method: "GET",
    credentials: 'include', 
  });

  if (!res.ok) throw new Error("Failed to fetch sizes");
  return res.json();
}

export async function getTiles() {
  const res = await fetch(`${API_BASE}/api/retailer/products/tiles`, {
    method: "GET",
    credentials: 'include',
  });

  if (!res.ok) throw new Error("Failed to fetch tiles");
  return res.json();
}

export async function getWallpapers() {
  const res = await fetch(`${API_BASE}/api/retailer/products/wallpapers`, {
    method: "GET",
    credentials: 'include', 
  });

  if (!res.ok) throw new Error("Failed to fetch wallpapers");
  return res.json();
}

export async function deleteTile(productId) {
  const res = await fetch(
    `${API_BASE}/api/retailer/products/tiles/${productId}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );

  if (!res.ok) throw new Error("Failed to delete tile");
  return res.json();
}

export async function deleteWallpaper(productId) {
  const res = await fetch(
    `${API_BASE}/api/retailer/products/wallpapers/${productId}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );

  if (!res.ok) throw new Error("Failed to delete wallpaper");
  return res.json();
}

export async function deleteSize(sizeId) {
  const res = await fetch(`${API_BASE}/api/retailer/products/sizes/${sizeId}`, {
    method: "DELETE",
    credentials: 'include', 
  });

  if (!res.ok) throw new Error("Failed to delete size");
  return res.json();
}

export async function editTile(productId, formData) {
  const res = await fetch(
    `${API_BASE}/api/retailer/products/tiles/${productId}`,
    {
      method: "PATCH",
      credentials: "include",
      body: formData,
    },
  );

  if (!res.ok) throw new Error("Failed to edit tile");
  return res.json();
}

export async function addTileRandomFace(productId, imageUrl) {
  const res = await fetch(
    `${API_BASE}/api/retailer/products/tiles/${productId}/random-faces`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        image_url: imageUrl,
      }),
    }
  );

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || "Failed to add tile image");
  }

  return res.json();
}

export async function filterBySize({ size, page = 1, limit = 12 }) {
  const params = new URLSearchParams({
    page,
    limit,
  });

  if (size && size !== "all") {
    params.append("size", size);
  }

  const res = await fetch(
    `${API_BASE}/api/retailer/products/tiles?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    },
  );

  if (!res.ok) throw new Error("Failed to fetch data");
  return res.json();
}

export async function editWallpaper(productId, formData) {
  const res = await fetch(
    `${API_BASE}/api/retailer/products/wallpapers/${productId}`,
    {
      method: "PATCH",
      credentials: "include",
      body: formData,
    },
  );

  if (!res.ok) throw new Error("Failed to edit wallpaper");
  return res.json();
}

export async function editSize(sizeId, formData) {
  const res = await fetch(`${API_BASE}/api/retailer/products/sizes/${sizeId}`, {
    method: "PATCH",
    credentials: 'include', 
    body: formData,
  });

  if (!res.ok) throw new Error("Failed to edit size");
  return res.json();
}

// Stock management apis
export async function getRetailerStock(params = {}) {
  const queryString = new URLSearchParams(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  ).toString();
  const res = await fetch(
    `${API_BASE}/api/retailer/stock${queryString ? `?${queryString}` : ""}`,
    {
      method: "GET",
      credentials: 'include', 
    },
  );

  if (!res.ok) throw new Error("Failed to fetch retailer stock");
  return res.json();
}

export async function getRetailerLowStock(params = {}) {
  const queryString = new URLSearchParams(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  ).toString();
  const res = await fetch(
    `${API_BASE}/api/retailer/stock/low${queryString ? `?${queryString}` : ""}`,
    {
      method: "GET",
      credentials: 'include', 
    },
  );

  if (!res.ok) throw new Error("Failed to fetch low stock products");
  return res.json();
}

// get retailer visualizer link
export async function getRetailerVisualizerLink() {
  const res = await fetch(`${API_BASE}/api/retailer/visualizer-link`, {
    method: "GET",
    credentials: 'include',
  });

  if (!res.ok) throw new Error("Failed to fetch visualizer link");
  return res.json();
}

export async function getPublicVisualizer(visualizerToken) {
  const res = await fetch(`${API_BASE}/api/visualizer/${visualizerToken}`, {
    method: "GET",
  });

  if (!res.ok) throw new Error("Failed to load visualizer");
  return res.json();
}

export async function getPublicVisualizerRetailer(visualizerToken) {
  const res = await fetch(
    `${API_BASE}/api/visualizer/${visualizerToken}/retailer`,
    {
      method: "GET",
    },
  );

  if (!res.ok) throw new Error("Failed to load retailer info");
  return res.json();
}

// get retailer products based on visualizer token
export async function getPublicVisualizerTiles(visualizerToken) {
  const res = await fetch(
    `${API_BASE}/api/visualizer/${visualizerToken}/tiles`,
    {
      method: "GET",
    },
  );

  if (!res.ok) throw new Error("Failed to load tiles");
  return res.json();
}

export async function getPublicVisualizerWallpapers(visualizerToken) {
  const res = await fetch(
    `${API_BASE}/api/visualizer/${visualizerToken}/wallpapers`,
    {
      method: "GET",
    },
  );

  if (!res.ok) throw new Error("Failed to load wallpapers");
  return res.json();
}

export async function searchPublicVisualizerProducts(
  visualizerToken,
  params = {},
) {
  const queryString = new URLSearchParams(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  ).toString();
  const res = await fetch(
    `${API_BASE}/api/visualizer/${visualizerToken}/products/search${queryString ? `?${queryString}` : ""}`,
    { method: "GET" },
  );

  if (!res.ok) throw new Error("Failed to search visualizer products");
  return res.json();
}

export async function getPublicVisualizerProductFilters(visualizerToken) {
  const res = await fetch(
    `${API_BASE}/api/visualizer/${visualizerToken}/products/filters`,
    {
      method: "GET",
    },
  );

  if (!res.ok) throw new Error("Failed to load visualizer product filters");
  return res.json();
}

// Admin Dashboard APIs
export async function getAdminDashboard() {
  const res = await fetch(`${API_BASE}/api/admin/dashboard`, {
    method: "GET",
    credentials: 'include',
  });

  if (!res.ok) throw new Error("Failed to fetch admin dashboard");
  return res.json();
}

export async function uploadCompanyLogo(logoFile) {
  const formData = new FormData();
  formData.append("logo", logoFile);

  const res = await fetch(`${API_BASE}/api/retailer/products/company-logo`, {
    method: "POST",
    credentials: 'include', 
    body: formData,
  });

  if (!res.ok) throw new Error("Failed to upload company logo");
  return res.json();
}

export async function getCompanyLogo() {
  const res = await fetch(`${API_BASE}/api/retailer/products/company-logo`, {
    method: "GET",
    credentials: 'include', 
  });

  if (!res.ok) throw new Error("Failed to fetch company logo");
  return res.json();
}

export async function adminLogin(formData) {
  const res = await fetch(`${API_BASE}/api/admin/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(formData),
  });

  if (!res.ok) throw new Error("Failed to login");
  return res.json();
}

export async function adminLogout() {
  const res = await fetch(`${API_BASE}/api/admin/auth/logout`, {
    method: "POST",
    credentials: 'include', 
  });

  if (!res.ok) throw new Error("Failed to logout");
  return res.json();
}

export async function getAdminRetailers(params = {}) {
  const queryString = new URLSearchParams(params).toString();
  const url = `${API_BASE}/api/admin/retailers${queryString ? `?${queryString}` : ""}`;

  const res = await fetch(url, {
    method: "GET",
    credentials: 'include', 
  });

  if (!res.ok) throw new Error("Failed to fetch retailers");
  return res.json();
}

export async function deleteAdminRetailer(retailerId) {
  const res = await fetch(`${API_BASE}/api/admin/retailers/${retailerId}`, {
    method: "DELETE",
    credentials: 'include', 
  });

  if (!res.ok) throw new Error("Failed to delete retailer");
  return res.json();
}

export async function getAdminRetailerStats(retailerId) {
  const res = await fetch(
    `${API_BASE}/api/admin/retailers/${retailerId}/stats`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!res.ok) throw new Error("Failed to fetch retailer stats");
  return res.json();
}

export async function getAdminRetailerActivity(retailerId) {
  const res = await fetch(
    `${API_BASE}/api/admin/retailers/${retailerId}/activity`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!res.ok) throw new Error("Failed to fetch retailer activity");
  return res.json();
}

export async function getAdminRetailerDetail(retailerId) {
  const res = await fetch(`${API_BASE}/api/admin/retailers/${retailerId}`, {
    method: "GET",
    credentials: 'include', 
  });

  if (!res.ok) throw new Error("Failed to fetch retailer detail");
  return res.json();
}

// Product QR Code
export async function generateProductQR(productId) {
  const res = await fetch(
    `${API_BASE}/api/retailer/products/${productId}/generate-qr`,
    {
      method: "POST",
      credentials: "include",
    },
  );

  if (!res.ok) throw new Error("Failed to generate QR code");
  return res.json();
}

export async function downloadProductQR(productId) {
  const res = await fetch(
    `${API_BASE}/api/retailer/products/${productId}/download-qr`,
    {
      method: "GET",
      credentials: "include",
    },
  );

  if (!res.ok) throw new Error("Failed to download QR code");

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `product_qr_${productId}.png`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);

  return { success: true };
}

// add to selection
export async function calculateSelection(payload) {
  const res = await fetch(`${API_BASE}/api/selection/calculate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new Error(errorData?.detail || "Calculation failed");
  }

  return res.json();
}

export async function sendQuotation(
  visualizerToken,
  wastagePercentage,
  items,
  totals,
) {
  const res = await fetch(`${API_BASE}/api/selection/send`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      visualizer_token: visualizerToken,
      wastage_percentage: wastagePercentage,
      items,
      totals,
    }),
  });

  if (!res.ok) throw new Error("Failed to send quotation to retailer");
  return res.json();
}

export async function getRetailerStats() {
  const [tilesRes, wallpapersRes, sizesRes] = await Promise.all([
    getTiles(),
    // getWallpapers(),
    getSizes(),
  ]);
 
  const tilesCount = tilesRes.success ? tilesRes.products.length : 0;
  const wallpapersCount = wallpapersRes.success ? wallpapersRes.products.length : 0;
  const sizesCount = sizesRes.success ? sizesRes.sizes.length : 0;
 
  return {
    success: true,
    stats: {
      totalProducts: tilesCount + wallpapersCount,
      totalTiles: tilesCount,
      totalWallpapers: wallpapersCount,
      totalSizes: sizesCount,
    },
  };
}


export { API_BASE, ASSET_BASE_URL };

export async function downloadProductsPdf(visualizerToken, productIds) {
  const response = await fetch(
    `${API_BASE}/api/visualizer/${visualizerToken}/download-pdf`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ product_ids: productIds }),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to generate PDF");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Product_Catalog.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function sendInquiry(visualizerToken, inquiryData) {
  const res = await fetch(
    `${API_BASE}/api/visualizer/${visualizerToken}/send-inquiry`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        visualizer_token: visualizerToken,
        ...inquiryData,
      }),
    },
  );

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.detail || "Failed to send inquiry");
  }

  return data;
}

export async function getRetailerInquiries(
  token,
  { page = 1, limit = 10, search = "", country = "" } = {}
) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (search) {
    params.append("search", search);
  }

  if (country) {
    params.append("country", country);
  }

  const response = await fetch(
    `${API_BASE}/api/retailer/inquiries?${params.toString()}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch inquiries");
  }

  return response.json();
}

export async function getRetailerInquiry(inquiryId) {
  const response = await fetch(
    `${API_BASE}/api/retailer/inquiries/${inquiryId}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch inquiry details");
  }

  return response.json();
}

export async function uploadCatalogFrontPage(file) {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_BASE}/api/catalog/settings/front-page`, {
    method: "POST",
    credentials: 'include', 
    body: formData,
  });

  if (!res.ok) throw new Error("Failed to upload catalog front page");
  return res.json();
}

export async function uploadCatalogLastPage(file) {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_BASE}/api/catalog/settings/last-page`, {
    method: "POST",
    credentials: 'include', 
    body: formData,
  });

  if (!res.ok) throw new Error("Failed to upload catalog last page");
  return res.json();
}

export async function getCatalogSettings() {
  // const token = localStorage.getItem("token"); // adjust key if stored under a different name (e.g. "accessToken")

  const res = await fetch(`${API_BASE}/api/catalog/settings`, {
    method: "GET",
    credentials: 'include',
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || "Failed to fetch catalog settings");
  }
  return res.json();
}

export async function addRoomToCatalog(payload) {
  const res = await fetch(`${API_BASE}/api/catalog/rooms`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));

    throw new Error(
      typeof errorData.detail === "string"
        ? errorData.detail
        : "Failed to add room to catalog"
    );
  }

  return res.json();
}

// services/api.js

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function downloadCatalogPdf(
  catalogId,
  sessionId
) {
  const response = await fetch(
    `${API_BASE}/api/catalog/${catalogId}/download?session_id=${sessionId}`,
    {
      method: "POST",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const errorData =
      await response.json().catch(() => ({}));

    throw new Error(
      errorData.detail ||
      "Failed to download catalog PDF"
    );
  }

  const blob = await response.blob();

  const disposition =
    response.headers.get(
      "Content-Disposition"
    ) || "";

  const match = disposition.match(
    /filename="?([^"]+)"?/i
  );

  const filename =
    match?.[1] ||
    `catalog-${catalogId}.pdf`;

  downloadBlob(blob, filename);
}
// Bulk Product Delete and Update 
export async function bulkUpdateSizes(payload) {
  const res = await fetch(
    `${API_BASE}/api/retailer/products/sizes/bulk-update`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    }
  );

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));

    const message =
      typeof errorData.detail === "object"
        ? errorData.detail.message
        : errorData.detail;

    throw new Error(message || "Failed to update selected sizes");
  }

  return res.json();
}

export async function bulkDeleteSizes(sizeIds) {
  const res = await fetch(
    `${API_BASE}/api/retailer/products/sizes/bulk-delete`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        size_ids: sizeIds,
      }),
    }
  );

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));

    const message =
      typeof errorData.detail === "object"
        ? errorData.detail.message
        : errorData.detail;

    throw new Error(message || "Failed to delete selected sizes");
  }

  return res.json();
}

/**
 * Fetch latest product stock details
 */
export async function getProductStock(productId) {
  const token = localStorage.getItem("retailer_token");
  const res = await fetch(`${API_BASE}/retailer/products/${productId}/stock`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.detail || "Failed to fetch product stock");
  }
  return data;
}

/**
 * Decrease product stock
 */
export async function decreaseProductStock(productId, payload) {
  const token = localStorage.getItem("retailer_token");
  const res = await fetch(`${API_BASE}/retailer/products/${productId}/decrease-stock`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload), // payload: { quantity, inquiry_id, note }
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.detail || "Failed to decrease stock");
  }
  return data;
}

/**
 * Fetch stock transaction history for a specific product
 */
export async function getProductStockHistory(productId) {
  const token = localStorage.getItem("retailer_token");
  const res = await fetch(`${API_BASE}/retailer/products/${productId}/stock-history`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.detail || "Failed to fetch stock history");
  }
  return data;
}

/**
 * Fetch retailer order/sales history with optional filters and pagination
 */
export async function getRetailerOrderHistory(params = {}) {
  const token = localStorage.getItem("retailer_token");

  // Construct query parameters
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.customer) queryParams.append("customer", params.customer);
  if (params.product_id) queryParams.append("product_id", params.product_id);
  if (params.inquiry_id) queryParams.append("inquiry_id", params.inquiry_id);

  const queryString = queryParams.toString();
  const url = `${API_BASE}/retailer/products/order-history${queryString ? `?${queryString}` : ""}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.detail || "Failed to fetch order history");
  }
  return data;
}


export async function downloadInquiryReport() {
  const token = localStorage.getItem("retailer_token");

  const res = await fetch(`${API_BASE}/api/retailer/inquiries/download-report`, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    // Parse error JSON response (e.g. 404 No inquiries found)
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.detail || "Failed to download report");
  }

  // Convert response stream to Blob for binary Excel data
  const blob = await res.blob();

  // Create an object URL and trigger standard browser file download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "inquiry_report.xlsx");
  document.body.appendChild(link);
  link.click();

  // Clean up DOM and memory reference
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);

  return { success: true };
}


export async function createSelection(selectionData) {
  const res = await fetch(`${API_BASE}/selection`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(selectionData),
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => null);
    console.error("Backend Validation Error:", errorBody);
    throw new Error(errorBody?.detail?.[0]?.msg || "Failed to create selection");
  }

  return res.json();
}

export async function updateSelection(selectionId, updateData) {
  const res = await fetch(`${API_BASE}/selection/${selectionId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(updateData),
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => null);
    console.error("Backend Validation Error:", errorBody);
    throw new Error(errorBody?.detail?.[0]?.msg || "Failed to update selection");
  }

  return res.json();
}

export async function getSelection(selectionId) {
  const url = selectionId
    ? `${API_BASE}/api/selection?id=${selectionId}`
    : `${API_BASE}/api/selection`;

  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) throw new Error("Failed to fetch selection data");
  return res.json();
}

// ../services/api.js

export async function getSelectionById(selectionId) {
  const res = await fetch(`${API_BASE}/selection/${selectionId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  if (!res.ok) throw new Error("Failed to fetch selection data");
  return res.json();
}

export const downloadQuotation = async (selectionId) => {
  const response = await fetch(
    `${API_BASE}/selection/download-quotation/${selectionId}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);

    throw new Error(
      errorData?.detail ||
        "Failed to download quotation"
    );
  }

  const blob = await response.blob();

  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");

  link.href = url;
  link.download = `quotation_${selectionId}.pdf`;

  document.body.appendChild(link);

  link.click();

  link.remove();

  window.URL.revokeObjectURL(url);
};